import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import MarkdownText from '../../components/foundations/MarkdownText';
import useTypingAnimation from '../hooks/useTypingAnimation';
import { MESSAGE_ROLES, ARTIFACT_TYPES } from '../constants';
import css from './ChatMessage.css';
import ArtifactWithInsights from './artifacts/ArtifactWithInsights';
import EphemeralUpdatesPanel from './EphemeralUpdatesPanel';
import Reasoning from './Reasoning';
import MessageFeedback from './feedback/MessageFeedback';

const ANALYZING_PLACEHOLDER_KEY = 'STATIC.PAGES.ANALYSIS_CHAT.AGENT_ACTIVITY.ANALYZING_PLACEHOLDER';

const ROLE_LABELS = {
    USER: 'You',
    ASSISTANT: 'AI Assistant',
};

const TEXT_MESSAGE_TYPE = 'markdown';

const ChatMessage = ({ message, sessionId, onDashboardClick, onFeedbackSubmitted, onFeedbackFailed }) => {
    const isUser = message.role === MESSAGE_ROLES.USER;
    const isAssistant = message.role === MESSAGE_ROLES.ASSISTANT;
    const { isStreaming } = message;

    // Check if content is empty (for initial thinking state). When a reasoning
    // chunk has already arrived, the Reasoning panel's "Thinking…" label is
    // the better signal — don't render the bouncing-dot placeholder on top of it.
    const hasReasoning = Array.isArray(message.reasoning) && message.reasoning.length > 0;
    const isInitialThinking =
        message.isThinking
        && !hasReasoning
        && (!message.content || (Array.isArray(message.content) && message.content.length === 0));

    const formattedTime = useMemo(() => (message.createdAt ? new Date(message.createdAt).toLocaleTimeString() : ''), [
        message.createdAt,
    ]);

    // Get current markdown content for typing animation (only for last markdown block in streaming)
    const currentTextForAnimation = useMemo(() => {
        if (!isAssistant || !isStreaming || !Array.isArray(message.content)) return '';

        for (let i = message.content.length - 1; i >= 0; i--) {
            if (message.content[i].type === TEXT_MESSAGE_TYPE) {
                return message.content[i].data || '';
            }
        }
        return '';
    }, [isAssistant, isStreaming, message.content]);

    // Use typing animation hook for assistant messages
    const animatedText = useTypingAnimation(currentTextForAnimation, isAssistant && isStreaming);

    const contentBlocks = useMemo(() => {
        if (!Array.isArray(message.content)) return [];

        return message.content.map((block, index) => {
            const isLastTextBlock =
                isStreaming &&
                block.type === TEXT_MESSAGE_TYPE &&
                index === message.content.findLastIndex(b => b.type === TEXT_MESSAGE_TYPE);
            const { data, ...rest } = block;

            return {
                ...rest,
                data: isLastTextBlock ? String(animatedText || '') : data,
                id: block.id || `${message.id}-block-${index}`,
            };
        });
    }, [message.content, message.id, isStreaming, animatedText]);

    const messageContentBlocks = contentBlocks.filter(block => !block.is_final_analysis);
    const previewContentBlocks = contentBlocks.filter(block => block.is_final_analysis);

    const hasErrorBlock = contentBlocks.some(block => block.type === ARTIFACT_TYPES.ERROR);
    const isFinished = message.isHistorical || Boolean(message.finishedAt);
    const shouldShowFeedback =
        isAssistant && isFinished && !hasErrorBlock && messageContentBlocks.length > 0;

    return (
        <div
            className={classNames(css.chatMessage, { [css.user]: isUser, [css.assistant]: isAssistant })}
        >
            <div className={css.chatMessageHeader}>
                <div className={css.chatMessageAvatar}>
                    <Icon name={isUser ? 'user' : 'robot'} size={IconSizes.SMALL} />
                </div>
                <div className={css.chatMessageRole}>{isUser ? ROLE_LABELS.USER : ROLE_LABELS.ASSISTANT}</div>
                <div className={css.chatMessageTime}>{formattedTime}</div>
            </div>
            <div className={css.chatMessageContent}>
                {isAssistant && message.reasoning?.length > 0 && (
                    <Reasoning
                        reasoning={message.reasoning}
                        isMessageActive={Boolean(message.isThinking)}
                    />
                )}
                {isInitialThinking ? (
                    <div className={css.chatMessageThinking}>
                        <span className={css.chatMessageThinkingDot} />
                        <span className={css.chatMessageThinkingText}>
                            <Translate id={ANALYZING_PLACEHOLDER_KEY} />
                        </span>
                    </div>
                ) : (
                    <>
                        {isUser && message.content && (
                            <MarkdownText
                                content={
                                    Array.isArray(message.content)
                                        ? String(message.content.find(b => b.type === TEXT_MESSAGE_TYPE)?.data || '')
                                        : String(message.content || '')
                                }
                            />
                        )}
                        {isAssistant && message.ephemeralUpdates?.length > 0 && (
                            <EphemeralUpdatesPanel updates={message.ephemeralUpdates} />
                        )}
                        {isAssistant && messageContentBlocks.length > 0 && (
                            <>
                                {messageContentBlocks.map(block => {
                                    const isWidget = block.type === ARTIFACT_TYPES.WIDGET;
                                    return (
                                        <div key={block.id} className={isWidget ? css.widget : css.artifact}>
                                            <ArtifactWithInsights artifact={block} />
                                        </div>
                                    );
                                })}
                                {previewContentBlocks.length > 0 && (
                                    <div
                                        className={css.previewDashboard}
                                        onClick={() => onDashboardClick(previewContentBlocks)}
                                    >
                                        <span>View Dashboard</span>
                                        <Icon
                                            name="arrow-right-fill"
                                            size={IconSizes.SMALL}
                                            variant={IconVariants.ACTIVE}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {shouldShowFeedback && (
                            <MessageFeedback
                                runId={message.runId}
                                sessionId={sessionId}
                                contentBlocks={messageContentBlocks}
                                onSubmitted={onFeedbackSubmitted}
                                onFailed={onFeedbackFailed}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

ChatMessage.propTypes = {
    message: PropTypes.shape({
        id: PropTypes.string.isRequired,
        runId: PropTypes.string,
        role: PropTypes.oneOf(['user', 'assistant', 'system']).isRequired,
        content: PropTypes.array, // Array of content blocks: [{type: 'markdown', data: '...'}, {type: 'code', data: {...}}, ...]
        createdAt: PropTypes.instanceOf(Date),
        isThinking: PropTypes.bool,
        isStreaming: PropTypes.bool,
        isHistorical: PropTypes.bool,
        finishedAt: PropTypes.number,
        ephemeralUpdates: PropTypes.array,
        reasoning: PropTypes.array,
        steps: PropTypes.array,
    }).isRequired,
    sessionId: PropTypes.string,
    onDashboardClick: PropTypes.func,
    onFeedbackSubmitted: PropTypes.func,
    onFeedbackFailed: PropTypes.func,
};

export default React.memo(ChatMessage);
